import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Popconfirm,
  message,
  Card,
  Row,
  Col,
  Divider,
  Checkbox
} from 'antd';
import {
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  MailOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import { subUserAPI, stationOwnerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';

const { Option } = Select;

interface SubUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: 'manager' | 'supervisor' | 'operator' | 'viewer';
  permissions: any;
  assignedStations: any[];
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
}

const SubUsers: React.FC = () => {
  const { userType } = useAuth();
  const [subUsers, setSubUsers] = useState<SubUser[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<SubUser | null>(null);
  const [form] = Form.useForm();

  const loadData = async () => {
    setLoading(true);
    try {
      const [subUsersData, stationsData] = await Promise.all([
        subUserAPI.getSubUsers(),
        stationOwnerAPI.getStations()
      ]);
      setSubUsers(subUsersData);
      setStations(stationsData);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Sadece ana kullanıcılar bu sayfaya erişebilir
    if (userType !== 'subuser') {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  // Sadece ana kullanıcılar bu sayfaya erişebilir
  if (userType === 'subuser') {
    return <Navigate to="/panel" replace />;
  }

  const handleCreate = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (user: SubUser) => {
    setEditingUser(user);
    // Sadece güncellenebilir alanları form'a set et
    form.setFieldsValue({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone || '',
      role: user.role,
      permissions: user.permissions || {},
      assignedStations: user.assignedStations?.map((s: any) => s._id || s) || []
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await subUserAPI.deleteSubUser(id);
      message.success('Alt kullanıcı silindi');
      loadData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Silme işlemi başarısız');
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      await subUserAPI.toggleSubUserStatus(id);
      message.success('Durum güncellendi');
      loadData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleVerify = async (id: string) => {
    try {
      await subUserAPI.verifySubUser(id);
      message.success('Kullanıcı onaylandı');
      loadData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Onaylama işlemi başarısız');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // Sadece güncellenebilir alanları gönder
      const submitData: any = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        role: values.role,
        permissions: values.permissions || {},
        assignedStations: values.assignedStations || []
      };
      
      // Şifre sadece yeni kullanıcı oluşturulurken gönderilir
      if (!editingUser && values.password) {
        submitData.password = values.password;
      }
      
      if (editingUser) {
        await subUserAPI.updateSubUser(editingUser._id, submitData);
        message.success('Alt kullanıcı güncellendi');
      } else {
        await subUserAPI.createSubUser(submitData);
        message.success('Alt kullanıcı oluşturuldu');
      }
      setModalVisible(false);
      form.resetFields();
      loadData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const getRoleColor = (role: string) => {
    const colors: any = {
      manager: 'red',
      supervisor: 'orange',
      operator: 'blue',
      viewer: 'green'
    };
    return colors[role] || 'default';
  };

  const getRoleName = (role: string) => {
    const names: any = {
      manager: 'Yönetici',
      supervisor: 'Süpervizör',
      operator: 'Operatör',
      viewer: 'Görüntüleyici'
    };
    return names[role] || role;
  };

  const columns: ColumnsType<SubUser> = [
    {
      title: 'Ad Soyad',
      dataIndex: 'firstName',
      key: 'name',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
      render: (email) => (
        <Space>
          <MailOutlined />
          {email}
        </Space>
      ),
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => phone ? (
        <Space>
          <PhoneOutlined />
          {phone}
        </Space>
      ) : '-',
    },
    {
      title: 'Rol',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={getRoleColor(role)}>{getRoleName(role)}</Tag>
      ),
    },
    {
      title: 'Durum',
      key: 'status',
      render: (_, record) => (
        <Space>
          <Tag color={record.isActive ? 'green' : 'red'}>
            {record.isActive ? 'Aktif' : 'Pasif'}
          </Tag>
          {!record.isVerified && (
            <Tag color="orange">Onay Bekliyor</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Düzenle
          </Button>
          {!record.isVerified && (
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={() => handleVerify(record._id)}
            >
              Onayla
            </Button>
          )}
          <Button
            type="link"
            danger={record.isActive}
            onClick={() => handleToggleStatus(record._id)}
          >
            {record.isActive ? 'Pasif Yap' : 'Aktif Yap'}
          </Button>
          <Popconfirm
            title="Bu kullanıcıyı silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record._id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <h2>Alt Kullanıcı Yönetimi</h2>
            <p style={{ color: '#666', margin: 0 }}>
              Firmalarınız için alt kullanıcılar oluşturun ve yönetin
            </p>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={handleCreate}
              size="large"
            >
              Yeni Kullanıcı Ekle
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={subUsers}
          loading={loading}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} kullanıcı`,
          }}
        />
      </Card>

      <Modal
        title={editingUser ? 'Alt Kullanıcı Düzenle' : 'Yeni Alt Kullanıcı Ekle'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={800}
        okText={editingUser ? 'Güncelle' : 'Oluştur'}
        cancelText="İptal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="Ad"
                rules={[{ required: true, message: 'Ad gereklidir' }]}
              >
                <Input placeholder="Ad" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Soyad"
                rules={[{ required: true, message: 'Soyad gereklidir' }]}
              >
                <Input placeholder="Soyad" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="E-posta"
                rules={[
                  { required: true, message: 'E-posta gereklidir' },
                  { type: 'email', message: 'Geçerli bir e-posta adresi giriniz' }
                ]}
              >
                <Input placeholder="email@example.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Telefon"
              >
                <Input placeholder="0555 555 55 55" />
              </Form.Item>
            </Col>
          </Row>

          {!editingUser && (
            <Form.Item
              name="password"
              label="Şifre"
              rules={[
                { required: true, message: 'Şifre gereklidir' },
                { min: 6, message: 'Şifre en az 6 karakter olmalıdır' }
              ]}
            >
              <Input.Password placeholder="Şifre" />
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="role"
                label="Rol"
                rules={[{ required: true, message: 'Rol seçiniz' }]}
              >
                <Select placeholder="Rol seçiniz">
                  <Option value="manager">Yönetici</Option>
                  <Option value="supervisor">Süpervizör</Option>
                  <Option value="operator">Operatör</Option>
                  <Option value="viewer">Görüntüleyici</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="assignedStations"
                label="Atanmış İstasyonlar"
                tooltip="Boş bırakılırsa tüm istasyonlara erişim sağlanır"
              >
                <Select
                  mode="multiple"
                  placeholder="İstasyon seçiniz"
                  allowClear
                >
                  {stations.map(station => (
                    <Option key={station._id} value={station._id}>
                      {station.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider>Yetkiler</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name={['permissions', 'canManageStations']} valuePropName="checked">
                <Checkbox>İstasyon Yönetimi</Checkbox>
              </Form.Item>
              <Form.Item name={['permissions', 'canViewStations']} valuePropName="checked">
                <Checkbox>İstasyon Görüntüleme</Checkbox>
              </Form.Item>
              <Form.Item name={['permissions', 'canEditStations']} valuePropName="checked">
                <Checkbox>İstasyon Düzenleme</Checkbox>
              </Form.Item>
              <Form.Item name={['permissions', 'canDeleteStations']} valuePropName="checked">
                <Checkbox>İstasyon Silme</Checkbox>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={['permissions', 'canManageCampaigns']} valuePropName="checked">
                <Checkbox>Kampanya Yönetimi</Checkbox>
              </Form.Item>
              <Form.Item name={['permissions', 'canViewCampaigns']} valuePropName="checked">
                <Checkbox>Kampanya Görüntüleme</Checkbox>
              </Form.Item>
              <Form.Item name={['permissions', 'canViewReports']} valuePropName="checked">
                <Checkbox>Rapor Görüntüleme</Checkbox>
              </Form.Item>
              <Form.Item name={['permissions', 'canViewAnalytics']} valuePropName="checked">
                <Checkbox>Analitik Görüntüleme</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default SubUsers;

